import { store } from '../../app/store';
import { PaginatedResult } from '../../models';
import RequestService from '../services/requestService';
import { TEntity, tEntityId } from '../../models/entity';
import {
    ApiType,
    createInjectedApi,
    FetchArg,
    getPatchDeleteEndpoint,
    getPatchUpdateEndpoint,
    ItemPropPatch,
    patchItemIntoCache,
    patchItemPropIntoCache,
    QueryAction,
    TApiEndpoints,
    TBaseQueryFn,
    TEntityDefinitions
} from './queryApiCreator';
import { BadRequestInfo } from '../../models/badRequestInfo';
import { QueryDefinition, QueryStatus } from '@reduxjs/toolkit/dist/query';
import { SerializedError } from '@reduxjs/toolkit';
import { isEmpty } from '../../app/utils/objectUtils';
import { QueryActionCreatorResult } from '@reduxjs/toolkit/dist/query/core/buildInitiate';

export class QueryApiService<T extends TEntity, U extends string, E extends TEntityDefinitions<T, U> = TEntityDefinitions<T, U>> {
    api: ApiType<T, U, E>;
    readonly serviceName: string;
    readonly typeName: U;
    readonly capitalizeTypeName: Capitalize<U>;
    private readonly unsubsribeHandlers: ({ endpoint: string; args: any; handler: () => void })[];


    constructor(service: RequestService<T, U>) {
        const tempApi = createInjectedApi<T, U>(service);
        this.serviceName = service.serviceName;
        this.typeName = service.typeName;
        this.capitalizeTypeName = service.capitalizeTypeName;
        this.unsubsribeHandlers = [];

        this.api = tempApi.injectEndpoints({
            endpoints: (build) => {
                const patchArgs = {
                    build: build,
                    updateQuery: (tempApi as any).util.updateQueryData,
                    serviceName: service.typeName,
                    idKey: service.idKey
                };
                const patchUpdateArgs = { ...patchArgs, requestPromise: (item: T) => service.update(item) };
                const patchDeleteArgs = { ...patchArgs, requestPromise: (id: tEntityId) => service.delete(id) };

                return {
                    [`${QueryAction.patchUpdate}${this.capitalizeTypeName}`]: getPatchUpdateEndpoint<T, U>(patchUpdateArgs),
                    [`${QueryAction.patchDelete}${this.capitalizeTypeName}`]: getPatchDeleteEndpoint<T, U>(patchDeleteArgs),
                };
            }
        }) as unknown as ApiType<T, U, E>;
    }


    get useCrudApiQueries() {
        const api = this.api as any;

        const endpoints = {
            fetch: api.endpoints[`${QueryAction.fetch}${this.capitalizeTypeName}`],
            filter: api.endpoints[`${QueryAction.filter}${this.capitalizeTypeName}`],
            create: api.endpoints[`${QueryAction.create}${this.capitalizeTypeName}`],
            search: api.endpoints[`${QueryAction.search}${this.capitalizeTypeName}`],
            update: api.endpoints[`${QueryAction.update}${this.capitalizeTypeName}`],
            delete: api.endpoints[`${QueryAction.delete}${this.capitalizeTypeName}`],
            deleteReport: api.endpoints[`${QueryAction.deleteReport}${this.capitalizeTypeName}`],
            patchUpdate: api.endpoints[`${QueryAction.patchUpdate}${this.capitalizeTypeName}`],
            patchDelete: api.endpoints[`${QueryAction.patchDelete}${this.capitalizeTypeName}`]

        } as TApiEndpoints<T, U>;

        return createApiQueriesHandler(endpoints, this.unsubsribeHandlers, this.capitalizeTypeName);
    }

    get handleResultPromise() {
        return async <TResult>(resultPromise: QueryActionCreatorResult<QueryDefinition<any, TBaseQueryFn<T>, any, TResult, string>>) => {
            var result = await resultPromise;
            if (resultPromise.unsubscribe && result.endpointName) {
                this.unsubsribeHandlers.push(({
                    endpoint: result.endpointName,
                    args: result.originalArgs,
                    handler: resultPromise.unsubscribe
                }));
            }
            return result;
        };
    }

    get unsubsribeQuery() {
        return (endpoint: keyof E, arg?: any) => {
            var handlers = this.unsubsribeHandlers.filter(i => i.endpoint === endpoint && (!arg || arg === i.args)).map(i => i.handler);
            handlers.forEach(unsubsribe => unsubsribe());
        };
    }

    get selectEntityById() {
        return async (itemId: T['id']) => {
            const result = await selectItemById<T>((this.api as any).endpoints[`${QueryAction.getById}${this.capitalizeTypeName}`], this.typeName, itemId);
            if (result?.unsubscribe) this.unsubsribeHandlers.push({
                endpoint: `${QueryAction.getById}${this.capitalizeTypeName}`,
                args: itemId,
                handler: result.unsubscribe
            });
            return result?.data;
        };
    }

    get optionsPlainSearch() {
        return async (searchString: string, predicate?: (value: T) => boolean, disablePagination: boolean = true) => {
            const result = (await store.dispatch((this.api as any).endpoints[`${QueryAction.search}${this.capitalizeTypeName}`].initiate({
                q: searchString,
                disablePagination: disablePagination,
                light: true
            }))).data?.value as T[];
            return result && predicate ? result.filter(predicate) : result ?? [];
        };
    }

    get advancedOptionsPlainSearch() {
        return async ({ disablePagination = true, ...param }: FetchArg<T>, predicate?: (value: T) => boolean) => {
            const result = (await store.dispatch((this.api as any).endpoints[`${QueryAction.fetch}${this.capitalizeTypeName}`].initiate({
                ...param,
                disablePagination,
                light: true
            }))).data?.value as T[];
            return result && predicate ? result.filter(predicate) : result ?? [];
        };
    }

    get getEntityFromCacheByTagId() {
        return (id: T['id']) => getItemFromCacheByTagId<T>(this.typeName, id, [`${QueryAction.fetch}${this.capitalizeTypeName}`, `${QueryAction.getById}${this.capitalizeTypeName}`]);
    }

    unsubsribe = () => this.unsubsribeHandlers.forEach(({ handler }) => handler());

    keepUnsubscribeHandler = (p: { endpoint: string; args: any; handler: () => void }) => this.unsubsribeHandlers.push(p);

    patchItemIntoCache = (item: T & Pick<T, 'id'>) => {
        return patchItemIntoCache({
            updateQuery: (this.api as any).util.updateQueryData,
            serviceName: this.typeName,
            item
        });
    };

    patchItemPropertyIntoCache = (patch: ItemPropPatch) => {
        return patchItemPropIntoCache({
            updateQuery: (this.api as any).util.updateQueryData,
            serviceName: this.typeName,
            patch
        });
    };

}

export async function selectItemById<T extends TEntity, tId extends tEntityId = T['id']>(getByIdEndpoint: TApiEndpoints<T, string, tId>[QueryAction.getById], serviceTypeName: string, itemId: tId) {
    if (itemId) {
        let data: T | null | undefined = getItemFromCacheByTagId<T>(serviceTypeName, itemId);

        if (!data) {
            const res = store.dispatch(getByIdEndpoint.initiate(itemId));
            data = await (await res).data;
            return { data, unsubscribe: res.unsubscribe };
        }

        return { data };
    }
}

export function provideTagList<R extends TEntity, T extends string>(resultsWithIds: R[] | undefined, tagType: T, additionalIdList?: string[], idKey?: keyof R) {
    const tagList = resultsWithIds
        ? [...resultsWithIds.map((item) => ({ type: tagType, id: typeof idKey !== 'undefined' ? item[idKey] as tEntityId : item.id })), tagType, { type: tagType, id: 'LIST' }]
        : [tagType];
    (additionalIdList ?? []).forEach(id => tagList.push({ type: tagType, id }));
    return tagList;
}

export function isSearchArgsCheck(args: FetchArg<any>) {
    return args.query != null &&
        ((typeof args.query === 'string' && args.query.length > 0) ||
            (typeof args.query === 'object' && !isEmpty(args.query)));
}

export function createApiQueriesHandler<T extends TEntity, U extends string>(
    apiEndpoints: TApiEndpoints<T, U>,
    unsubsribeHandlers: ({ endpoint: string; args: any; handler: () => void })[],
    capitalizeTypeName: string) {

    return (target: { args: FetchArg<T> }) => {

        const isSearchMode = isSearchArgsCheck(target.args);
        const fetchResult = apiEndpoints[QueryAction.fetch].useQuery(target.args, { skip: target?.args.skip });
        const [createItem, {
            isLoading: creation,
            error: errorCreating,
            reset: resetCreating
        }] = apiEndpoints[QueryAction.create].useMutation();

        const [deleteItem, {
            isLoading: deleting,
            error: errorDeleting,
        }] = apiEndpoints[QueryAction.delete].useMutation();

        const [updateItem, {
            isLoading: updating,
            error: errorUpdate,
            isSuccess: isSuccessUpdated,
            reset: resetUpdate
        }] = apiEndpoints[QueryAction.update].useMutation();

        //patchUpdate and patchDeleteItem work using provided tags,
        //if list query result doesn't provide id tags list won't update after mutation
        //(use providesTags: in query-endpoint)
        const [patchUpdate, {
            isLoading: patchUpdating,
            error: errorPatchUpdate,
            isSuccess: isSuccessPatchUpdated,
            reset: resetPatch
        }] = apiEndpoints[QueryAction.patchUpdate].useMutation();
        const [patchDeleteItem, { isLoading: patchDeleting }] = apiEndpoints[QueryAction.patchDelete].useMutation();

        function getErrors(): BadRequestInfo | undefined {
            var errors = [errorDeleting, errorCreating, errorPatchUpdate, errorUpdate].filter(i => i);
            if (errors.length > 0) {
                let extraInfoList: string[] = errors.map(i => i?.message ?? '').filter(i => i.length > 0);
                return { extraInfoList } as BadRequestInfo;
            }
        }

        const mutationErrors: BadRequestInfo | undefined = getErrors();

        function resetMutation() {
            resetCreating();
            resetUpdate();
            resetPatch();
        }

        function refetch(args: FetchArg<T>) {
            store.dispatch(apiEndpoints[QueryAction.fetch].initiate(args, { subscribe: false, forceRefetch: true }));
        }

        function unsubsribeHandler(endpoint: string, arg?: any) {
            var handlers = unsubsribeHandlers.filter(i => i.endpoint === endpoint && (!arg || arg === i.args)).map(i => i.handler);
            handlers.forEach(unsubsribe => unsubsribe());
        }

        async function deleteEntity(id: string | number) {
            unsubsribeHandler(`${QueryAction.deleteReport}${capitalizeTypeName}`, id);
            await deleteItem(id);
        }

        async function deleteReport(id: tEntityId): Promise<{ status: QueryStatus, error?: SerializedError }> {
            var result = store.dispatch(apiEndpoints[QueryAction.deleteReport].initiate(id));
            var data = await result;
            if (data?.endpointName && result?.unsubscribe) {
                unsubsribeHandlers.push({
                    endpoint: data.endpointName,
                    args: data.originalArgs,
                    handler: result.unsubscribe
                });
            }
            return { status: data.status, error: data.error };
        }

        return {
            fetchResult,
            createItem,
            updateItem,
            deleteItem: deleteEntity,
            isSuccessUpdated: isSuccessUpdated || isSuccessPatchUpdated,
            patchDeleteItem,
            deleteReport,
            patchUpdate,
            mutationErrors,
            resetMutation,
            isMutation: creation || updating || patchUpdating,
            isDeleting: deleting || patchDeleting,
            loading: fetchResult.isFetching || fetchResult.isUninitialized || deleting || patchDeleting,
            errors: fetchResult.error?.message ? [fetchResult.error.message] : [],
            refetch,
            isSearchMode
        };

    };

}

export function getItemFromCacheByTagId<T extends TEntity, tId extends tEntityId = T['id']>(serviceTypeName: string, itemId: tId, endpoints?: string[]) {
    if (itemId) {
        const queryCacheKey = store.getState().api.provided[serviceTypeName]?.[itemId];
        for (const cacheKey of queryCacheKey ?? []) {
            const subState = store.getState().api.queries[cacheKey];
            if (subState?.endpointName && ((endpoints && endpoints.includes(subState.endpointName)) || !endpoints)) {
                const entityTId = subState?.data as T;
                if (entityTId && entityTId.id) {
                    return entityTId;
                }

                const data = subState?.data as PaginatedResult<T>;
                if (subState && data?.value?.length > 0 && data?.value[0]?.id) {
                    const id = typeof data.value[0].id === 'number' ? Number(itemId) : itemId;
                    const item = data.value.find(value => value?.id === id);
                    return item;
                }
            }
        }
    }
}
